import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedService } from './sharedService';
import { PagerDataObject } from '../entities/pagerDataObject';

@Injectable()
export class RiskService {

  constructor(private http: HttpClient, private sharedService: SharedService) {}

  // Paginated risk list: GET /api/risk?year=&sector=&from=&size=...
  getData(filter: string): Observable<PagerDataObject> {
    return this.http.get<PagerDataObject>(this.sharedService.apiUrl('api/risk', filter));
  }

  // Control-module risk list (used by step-tabber and new-preprocess)
  getTotalRiskList(): Observable<any[]> {
    return this.http.get<any[]>(this.sharedService.apiUrl(this.sharedService.control_api_prf + '/process/risk/list'));
  }

  // Distinct years that have risk-management-enabled indicators
  getYears(): Observable<string[]> {
    return this.http.get<string[]>(this.sharedService.apiUrl('api/risk/years', ''));
  }

  // Per-taxpayer breakdown: which indicators contributed and how much
  getBreakdown(year: string, sector: string, match: string): Observable<any[]> {
    const params = `?year=${year}&sector=${encodeURIComponent(sector)}&match=${encodeURIComponent(match)}`;
    return this.http.get<any[]>(this.sharedService.apiUrl('api/risk/breakdown', params));
  }

  // Company list with total + per-indicator scores: GET /api/risk/company-scores
  getCompanyScores(filter: string): Observable<any> {
    return this.http.get<any>(this.sharedService.apiUrl('api/risk/company-scores', filter));
  }

  // Excel export of company scores with per-indicator columns
  exportCompanyScores$(filter: string): Observable<Blob> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Accept': 'application/vnd.ms-excel', 'Content-Type': 'application/vnd.ms-excel' }),
      responseType: 'blob' as 'json'
    };
    return this.http.get<any>(this.sharedService.apiUrl('api/risk/company-scores/export', filter), httpOptions).pipe(
      map((res: Response) => res),
      catchError(this.sharedService.handleError<any>('export company scores'))
    );
  }


  // Columns for the main risk list table
  columnsResults: Array<any> = [
    { key: 'view', style: { 'flex': '0 0 80px', 'width': '1%' }, button: true },
    { name: 'Tax ID',       tag: 'RISK.TAX_ID',       key: 'match',       filtering: true, sorting: true, sortingOrder: 'asc' },
    { name: 'Company Name', tag: 'RISK.COMPANY_NAME',  key: 'companyName', filtering: true, sorting: true },
    { name: 'Total Score',  tag: 'RISK.SCORE',         key: 'score',       filtering: false, sorting: true, sortingOrder: 'desc', isNumber: true },
    { name: 'Risk Level',   tag: 'RISK.LEVEL',         key: 'color',       filtering: false, sorting: false },
  ];

  // Columns for the breakdown drilldown table
  columnsBreakdown: Array<any> = [
    { name: 'Indicator',         tag: 'RISK.INDICATOR',    key: 'name',         filtering: false, sorting: true, sortingOrder: 'asc' },
    { name: 'Value',             tag: 'RISK.VALUE',        key: 'compareValue', filtering: false, sorting: true, isNumber: true },
    { name: 'Band (1/2/3)',      tag: 'RISK.BAND',         key: 'band',         filtering: false, sorting: true, isNumber: true },
    { name: 'Ponderation',       tag: 'RISK.PONDERATION',  key: 'ponderation',  filtering: false, sorting: true, isNumber: true },
    { name: 'Contribution',      tag: 'RISK.CONTRIBUTION', key: 'contribution', filtering: false, sorting: true, sortingOrder: 'desc', isNumber: true },
  ];

  getColumnsResults(): Array<any> { return this.columnsResults; }
  getMetaResultsInfo(): string[] { return ['match', 'companyName', 'score', 'color']; }

  getColumnsBreakdown(): Array<any> { return this.columnsBreakdown; }
  getMetaBreakdownInfo(): string[] { return ['name', 'compareValue', 'band', 'ponderation', 'contribution']; }

  // Sector list — name is stored value, label is shown in dropdowns
  getSectors(): Array<{ name: string, label: string }> {
    return [
      { name: 'Default', label: 'Default' },
      { name: 'IFU1',    label: 'IFU1: SECTEUR INDUSTRIE' },
      { name: 'IFU2',    label: 'IFU2: SECTEUR COMMERCE GENERAL' },
      { name: 'IFU3',    label: 'IFU3: SECTEUR BANQUES, ASSURENCES ET NTIC' },
      { name: 'IFU4',    label: 'IFU4: FORETS, PETROLE, MINE' },
      { name: 'IFU5',    label: 'IFU5: TRANSPORT, HOTELERIE, SERVICE' },
    ];
  }
}
